/*
 * CARVERHAUS gallery rail (product media "One per row, with thumbnails").
 *
 * A sticky column of thumbnails beside a single-column grid of photos.
 * Clicking a thumbnail scrolls its photo into view; while the page scrolls,
 * the thumbnail of the photo nearest the top of the viewport is marked current.
 * Thumbnail N maps to grid item N (the finish close-up, when shown, is last).
 */
if (!customElements.get('ch-gallery-rail')) {
  customElements.define(
    'ch-gallery-rail',
    class ChGalleryRail extends HTMLElement {
      connectedCallback() {
        this.gallery = this.closest('media-gallery');
        this.items = [...(this.gallery?.querySelectorAll('.media-gallery__grid > li') ?? [])];
        this.thumbs = [...this.querySelectorAll('[data-ch-rail-index]')];
        if (!this.items.length) return;

        this.addEventListener('click', this.#onClick);

        this.visible = new Map();
        this.observer = new IntersectionObserver(this.#onIntersect, {
          rootMargin: '-35% 0px -55% 0px',
          threshold: 0,
        });
        this.items.forEach((item) => this.observer.observe(item));
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.#onClick);
        this.observer?.disconnect();
      }

      #setCurrent(index) {
        this.thumbs.forEach((thumb) => {
          const current = Number(thumb.dataset.chRailIndex) === index;
          if (current) {
            thumb.setAttribute('aria-current', 'true');
            /* Keep the current thumbnail inside the rail when it scrolls */
            const track = thumb.parentElement;
            const top = thumb.offsetTop - track.offsetTop;
            if (top < track.scrollTop || top + thumb.offsetHeight > track.scrollTop + track.clientHeight) {
              track.scrollTo({ top: top - track.clientHeight / 2 + thumb.offsetHeight / 2, behavior: 'smooth' });
            }
          } else {
            thumb.removeAttribute('aria-current');
          }
        });
      }

      #onClick = (event) => {
        const thumb = /** @type {HTMLElement} */ (event.target).closest('[data-ch-rail-index]');
        if (!thumb) return;
        const index = Number(thumb.dataset.chRailIndex);
        const item = this.items[index];
        if (!item) return;
        this.#setCurrent(index);
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        item.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      };

      #onIntersect = (entries) => {
        for (const entry of entries) {
          const index = this.items.indexOf(/** @type {HTMLElement} */ (entry.target));
          if (entry.isIntersecting) this.visible.set(index, true);
          else this.visible.delete(index);
        }
        if (this.visible.size) this.#setCurrent(Math.min(...this.visible.keys()));
      };
    }
  );
}
